import paper from 'paper';

import {
  Annotation,
  AnnotationCategory,
  AnnotationWithoutComputedProperties,
  ImageAnnotationWithoutComputedProperties,
  PathAnnotationWithoutComputedProperties,
  Point2D,
  ShapeAnnotationWithoutComputedProperties,
  TextAnnotationWithoutComputedProperties,
  VariableTranslationArea,
} from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { annotationIsEditableByUser } from '../../selectors/annotations';
import { UserData } from '../../store/users';
import { GripName } from './annotationGrips';

const POINTS_IN_AN_INCH = 72;

export interface PageImageInfo {
  readonly dpi: number;
  readonly pageImageWidth: number;
  readonly pageImageHeight: number;
}

// For information on how to install additional fonts, see https://trello.com/c/s5RZBXCo/199-how-to-add-fonts-to-pdf-editor
export const ENABLED_FONTS = {
  'IBM Plex Sans': 'IBM Plex Sans, sans-serif',
};

export interface RenderingItem extends paper.Item {
  reduxSelected: boolean;
  category: AnnotationCategory | undefined;
  guid: string;
  creatorEmail: string | null;
  annotationIsEditableByCurrentUser: boolean;
  restrictOrthogonal?: boolean;
  translationArea?: VariableTranslationArea;
  shouldBeRegisteredForEvents?: boolean;
  translationAreaBox?: paper.Path.Rectangle;
}

export interface RenderedItem extends paper.Item {
  reduxSelected: boolean;
  readonly category: AnnotationCategory | undefined;
  readonly guid: string;
  readonly creatorEmail: string | null;
  readonly annotationIsEditableByCurrentUser: boolean;
  readonly restrictOrthogonal?: boolean;
  readonly translationArea?: VariableTranslationArea;
  readonly shouldBeRegisteredForEvents?: boolean;
  readonly translationAreaBox?: paper.Path.Rectangle;

  beingDragged?: RenderedItem | false | null;
  gripBeingDragged?: GripName | null | undefined;
}

export function renderTranslationAreaBounds(item: RenderingItem, pageImageInfo: PageImageInfo) {
  if (item.translationAreaBox) {
    item.translationAreaBox.remove();
  }

  const translationAreaInPixels = {
    width: pointsToPixels(item.translationArea!.width, pageImageInfo.dpi),
    height: pointsToPixels(item.translationArea!.height, pageImageInfo.dpi),
  };

  const xExpansion = translationAreaInPixels.width - item.bounds.width;
  const yExpansion = translationAreaInPixels.height - item.bounds.height;

  const translationAreaBounds = item.bounds.clone().expand(xExpansion, yExpansion);
  const translationAreaBox = new paper.Path.Rectangle(translationAreaBounds);

  translationAreaBox.strokeWidth = 1;
  translationAreaBox.strokeColor = 'grey';

  item.translationAreaBox = translationAreaBox;
  item.addChild(translationAreaBox);
}

export type AnnotationWithSelectionData = Annotation & { readonly selected: boolean };

export function renderAnnotation(
  annotation: AnnotationWithSelectionData,
  userData: UserData,
  currentUserEmail: string,
  systemAnnotationsEditable: boolean,
  pageImageInfo: PageImageInfo,
): RenderedItem {
  const item = annotationToPaperItem(annotation, pageImageInfo) as RenderingItem;

  // The paper selected state is used for both showing selectability and selection.
  //  The redux store tells us what is selected. On hover events this allows us to distinguish.
  item.reduxSelected = annotation.selected;
  item.locked = annotation.locked;
  item.category = annotation.category;
  item.guid = annotation.guid;
  item.creatorEmail = annotation.creatorEmail;
  item.annotationIsEditableByCurrentUser = false;
  item.scaling = (annotation as any).scale;

  const annotationCreator = annotation.creatorEmail != null ? userData[annotation.creatorEmail] : null;
  const currentUser = userData[currentUserEmail];

  if (annotationIsEditableByUser(annotation, currentUser, userData, systemAnnotationsEditable)) {
    item.annotationIsEditableByCurrentUser = true;
  } else if (annotation.creatorEmail != null && annotationCreator && annotation.category !== 'system') {
    // Indicate other users' annotations with always visible selection boxes of different colors.
    item.selectedColor = userData[annotation.creatorEmail].annotationColor ?? null;
    item.selected = true;
  }

  if ('restrictOrthogonal' in annotation) {
    item.restrictOrthogonal = annotation.restrictOrthogonal;
  }

  // Added dashed line to annotations from different organizations
  if (userData && annotation.creatorEmail != null && userData[annotation.creatorEmail] && userData[currentUserEmail]) {
    const annotationOrganization = userData[annotation.creatorEmail].organization;
    const currentUserOrganization = userData[currentUserEmail].organization;
    if (annotationOrganization !== currentUserOrganization) {
      // get local rotation
      const { rotation } = item;
      item.rotation = 0;
      const { topLeft, topRight, bottomLeft, bottomRight } = item.strokeBounds;
      const dashedBorder = new paper.Path({
        segments: [
          [topLeft.x, topLeft.y],
          [topRight.x, topRight.y],
          [bottomRight.x, bottomRight.y],
          [bottomLeft.x, bottomLeft.y],
          [topLeft.x, topLeft.y],
        ],
        selected: false,
      });
      // restore rotation
      item.rotation = rotation;
      dashedBorder.rotation = item.rotation;
      dashedBorder.selectedColor = item.selectedColor;
      dashedBorder.strokeColor = item.selectedColor;
      dashedBorder.strokeWidth = 2;
      dashedBorder.strokeCap = 'round';
      dashedBorder.dashArray = [10, 12];
      item.selected = false;
      item.addChild(dashedBorder);
    }
  }

  if ('translationArea' in annotation && annotation.translationArea) {
    item.translationArea = annotation.translationArea;

    if (annotation.selected) {
      renderTranslationAreaBounds(item, pageImageInfo);
    }
  }

  if (systemAnnotationsEditable || annotation.category !== 'system') {
    item.shouldBeRegisteredForEvents = true;
  }

  return item;
}

export function getBackgroundRasterBetweenPoints(startDragPoint: Point2D, endDragPoint: Point2D) {
  const backgroundRaster = paper.project.activeLayer.children[0] as paper.Raster;
  const rasterTopLeftCorner = new paper.Point(backgroundRaster.bounds.topLeft);

  const adjustedStartDragPoint = new paper.Point(
    startDragPoint.x - rasterTopLeftCorner.x,
    startDragPoint.y - rasterTopLeftCorner.y,
  );

  const adjustedEndDragPoint = new paper.Point(
    endDragPoint.x - rasterTopLeftCorner.x,
    endDragPoint.y - rasterTopLeftCorner.y,
  );

  const selectedAreaRectangle = new paper.Rectangle(adjustedStartDragPoint, adjustedEndDragPoint);
  return backgroundRaster.getSubRaster(selectedAreaRectangle);
}

export function getPositionInPdfCoordinates(position: Point2D, pageImageInfo: PageImageInfo) {
  const point = new paper.Point(position.x, position.y);
  const positionInPageImageCoordinates = paper.project.activeLayer.children[0].parentToLocal(point);

  return pageImageCoordinatesToPdfCoordinates(positionInPageImageCoordinates, pageImageInfo);
}

// The second step of getPositionInPdfCoordinates.  Must be broken out into a separate function for use in
//   migrating old annotations.  Other than that, you should probably never be using this method.  If there's ever a
//   situation in which getPositionInPdfCoordinates doesn't cover your needs, something is seriously broken.
export function pageImageCoordinatesToPdfCoordinates(position: Point2D, pageImageInfo: PageImageInfo) {
  const halfPageImageWidth = pageImageInfo.pageImageWidth / 2;
  const halfPageImageHeight = pageImageInfo.pageImageHeight / 2;
  const centerPositionInPageImageCoordinatesOriginBottomLeft = {
    x: halfPageImageWidth + position.x,
    y: halfPageImageHeight - position.y,
  };

  return {
    x: pixelsToPoints(centerPositionInPageImageCoordinatesOriginBottomLeft.x, pageImageInfo.dpi),
    y: pixelsToPoints(centerPositionInPageImageCoordinatesOriginBottomLeft.y, pageImageInfo.dpi),
  };
}

export function getPositionInPaperCoordinates(position: Point2D, pageImageInfo: PageImageInfo) {
  // Translate from annotation understanding of position (points in document from bottom left) to
  //   raster coordinates from bottom left (raster has its own DPI, points are 72 DPI)
  const positionInPageImageCoordinatesOriginBottomLeft = {
    x: pointsToPixels(position.x, pageImageInfo.dpi),
    y: pointsToPixels(position.y, pageImageInfo.dpi),
  };

  // Now translate the position to the way Paper.js does raster coordinates (origin in middle and
  //   weird quadrants)
  const halfPageImageWidth = pageImageInfo.pageImageWidth / 2;
  const halfPageImageHeight = pageImageInfo.pageImageHeight / 2;
  const positionInPageImageCoordinates = {
    x: positionInPageImageCoordinatesOriginBottomLeft.x - halfPageImageWidth,
    y: (positionInPageImageCoordinatesOriginBottomLeft.y - halfPageImageHeight) * -1,
  };

  const pdfPageBackgroundImage = paper.project.activeLayer.children[0];
  const annotationPoint = new paper.Point(positionInPageImageCoordinates.x, positionInPageImageCoordinates.y);

  return pdfPageBackgroundImage.localToParent(annotationPoint);
}

export function annotationToPaperItem(
  textAnnotation: TextAnnotationWithoutComputedProperties,
  pageImageInfo: PageImageInfo,
): paper.PointText;
export function annotationToPaperItem(
  imageAnnotation: ImageAnnotationWithoutComputedProperties,
  pageImageInfo: PageImageInfo,
): paper.Raster;
export function annotationToPaperItem(
  shapeAnnotation: ShapeAnnotationWithoutComputedProperties,
  pageImageInfo: PageImageInfo,
): paper.Shape.Rectangle;
export function annotationToPaperItem(
  pathAnnotation: PathAnnotationWithoutComputedProperties,
  pageImageInfo: PageImageInfo,
): paper.Path;
export function annotationToPaperItem(
  annotation: AnnotationWithoutComputedProperties,
  pageImageInfo: PageImageInfo,
): paper.Item;
export function annotationToPaperItem(annotation: AnnotationWithoutComputedProperties, pageImageInfo: PageImageInfo) {
  switch (annotation.type) {
    case 'text':
      return textToPaperItem(annotation, pageImageInfo);
    case 'image':
      return imageToPaperItem(annotation, pageImageInfo);
    case 'shape':
      return shapeToPaperItem(annotation, pageImageInfo);
    case 'path':
      return pathToPaperItem(annotation, pageImageInfo);
    default:
      assertExhaustiveSwitch(annotation, 'Unrecognized annotation type');
  }
}

export function textToPaperItem(annotation: TextAnnotationWithoutComputedProperties, pageImageInfo: PageImageInfo) {
  const fontSize = pointsToPixels(annotation.fontSize, pageImageInfo.dpi);
  const leading = pointsToPixels(annotation.leading, pageImageInfo.dpi);

  const fontWeightOptions = [];
  if (annotation.isBold) {
    fontWeightOptions.push('Bold');
  }

  if (annotation.isItalic) {
    fontWeightOptions.push('Italic');
  }

  const paperPointText = new paper.PointText({
    content: annotation.contents,
    fontSize,
    fontFamily: ENABLED_FONTS[annotation.fontFamily as keyof typeof ENABLED_FONTS] || 'sans-serif',
    fontWeight: fontWeightOptions.join(' '),
    leading,
    fillColor: annotation.color,
    selected: annotation.selected,
    rotation: -annotation.rotation,
    underline: annotation.isUnderlined,
  });

  paperPointText.position = getPositionInPaperCoordinates(annotation.position, pageImageInfo);

  if (annotation.selected) {
    paperPointText.selectedColor = 'green';
  }

  return paperPointText;
}

export function imageToPaperItem(annotation: ImageAnnotationWithoutComputedProperties, pageImageInfo: PageImageInfo) {
  const paperRaster = new paper.Raster({
    crossOrigin: 'anonymous',
    source: annotation.source,
    selected: annotation.selected,
    rotation: -annotation.rotation,
  });

  paperRaster.position = getPositionInPaperCoordinates(annotation.position, pageImageInfo);
  if (annotation.selected) paperRaster.selectedColor = 'green';

  return paperRaster;
}

export function shapeToPaperItem(annotation: ShapeAnnotationWithoutComputedProperties, pageImageInfo: PageImageInfo) {
  if (annotation.shapeType !== 'rectangle') {
    console.error(`Warning: invalid shape type encountered: '${annotation.shapeType}'`);
  }

  const width = annotation.width * (pageImageInfo.dpi / POINTS_IN_AN_INCH);
  const height = annotation.height * (pageImageInfo.dpi / POINTS_IN_AN_INCH);

  const paperShape = new paper.Shape.Rectangle({
    size: [width, height],
    strokeColor: annotation.color,
    selected: annotation.selected,
    fillColor: annotation.color,
    rotation: -annotation.rotation,
  });

  paperShape.position = getPositionInPaperCoordinates(annotation.position, pageImageInfo);
  if (annotation.selected) paperShape.selectedColor = 'green';

  return paperShape;
}

export function pathToPaperItem(annotation: PathAnnotationWithoutComputedProperties, pageImageInfo: PageImageInfo) {
  const segmentsInPaperCoordinates = annotation.segments.map((point) =>
    getPositionInPaperCoordinates(point, pageImageInfo),
  );

  let dashArray;
  if (annotation.strokeStyle === 'dashed') {
    // These values mimic PdfSharp dashed lines, which are not configurable in this manner
    dashArray = [3, 3];
  }

  const paperPath = new paper.Path({
    segments: segmentsInPaperCoordinates,
    selected: annotation.selected,
    strokeColor: annotation.color,
    rotation: -annotation.rotation,
    strokeWidth: annotation.strokeWidth ? pointsToPixels(annotation.strokeWidth, pageImageInfo.dpi) : 1,
    dashArray,
  });

  // Some paths are initially defined only by their segments and so won't have a separate center position
  if (annotation.position) {
    paperPath.position = getPositionInPaperCoordinates(annotation.position, pageImageInfo);
  }

  if (annotation.selected) paperPath.selectedColor = 'green';

  return paperPath;
}

export function pixelsToPoints(sizeInPixels: number, dpi: number) {
  const percentageOfAnInch = sizeInPixels / dpi;
  return percentageOfAnInch * POINTS_IN_AN_INCH;
}

export function pointsToPixels(sizeInPoints: number, dpi: number) {
  const percentageOfAnInch = sizeInPoints / POINTS_IN_AN_INCH;
  return percentageOfAnInch * dpi;
}

export function getBoundingBoxFromItem(item: paper.Item, pageImageInfo: PageImageInfo) {
  return {
    topLeft: getPositionInPdfCoordinates(item.bounds.topLeft, pageImageInfo),
    bottomLeft: getPositionInPdfCoordinates(item.bounds.bottomLeft, pageImageInfo),
    topRight: getPositionInPdfCoordinates(item.bounds.topRight, pageImageInfo),
    bottomRight: getPositionInPdfCoordinates(item.bounds.bottomRight, pageImageInfo),
  };
}

export function getBoundingBoxFromWidthHeightAndCenterPoint(width: number, height: number, centerPoint: Point2D) {
  const halfWidth = width / 2;
  const halfHeight = height / 2;

  return {
    topLeft: {
      x: centerPoint.x - halfWidth,
      y: centerPoint.y + halfHeight,
    },
    bottomLeft: {
      x: centerPoint.x - halfWidth,
      y: centerPoint.y - halfHeight,
    },
    topRight: {
      x: centerPoint.x + halfWidth,
      y: centerPoint.y + halfHeight,
    },
    bottomRight: {
      x: centerPoint.x + halfWidth,
      y: centerPoint.y - halfHeight,
    },
  };
}
